// assets/free-shipping-progress.js
(() => {
  'use strict';

  class FreeShippingProgress extends HTMLElement {
    constructor() {
      super();

      // data-threshold is in pounds (e.g. "10" or "£10")
      const raw = String(this.dataset.threshold || '').trim().replace(/[^\d.]/g, '');
      const t = Number.parseFloat(raw);
      this.threshold = Number.isFinite(t) && t > 0 ? Math.round(t * 100) : 0;

      this.messageBefore =
        this.dataset.messageBefore || 'Spend [amount] more for complimentary UK delivery';
      this.messageAfter =
        this.dataset.messageAfter || 'You qualify for complimentary UK delivery';

      this._subtotalEl = null;
      this._subtotalObs = null;
      this._containerObs = null;

      this._lastTotal = null;
      this._initialized = false;

      this._kick = null;

      this._retryTimer = null;
      this._retryCount = 0;

      // Track whether we have ever successfully painted UI
      this._hasRendered = false;
    }

    connectedCallback() {
      this._waitForSubtotal();

      this._kick = () => {
        this._findAndBindSubtotal();
        this._updateFromSources();
      };

      document.addEventListener('cart:updated', this._kick);
      document.addEventListener('cart:refresh', this._kick);
      document.addEventListener('cart:changed', this._kick);

      // First render: queue to next tick so inner DOM is present
      setTimeout(() => this._updateFromSources(), 0);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._kick);
      document.removeEventListener('cart:refresh', this._kick);
      document.removeEventListener('cart:changed', this._kick);

      if (this._subtotalObs) this._subtotalObs.disconnect();
      if (this._containerObs) this._containerObs.disconnect();

      this._subtotalObs = null;
      this._containerObs = null;
      this._subtotalEl = null;

      this._initialized = false;
      this._kick = null;

      // reset render state for next connection
      this._hasRendered = false;
      this._lastTotal = null;

      clearTimeout(this._retryTimer);
      this._retryTimer = null;
      this._retryCount = 0;
    }

    // Public safe kick method (so observers can call it without touching internals)
    requestUpdate() {
      this._findAndBindSubtotal();
      this._updateFromSources();
    }

    _waitForSubtotal() {
      this._findAndBindSubtotal();

      if (this._initialized) {
        this._updateFromSources();
        return;
      }

      // Observe local container for late-rendered subtotals inside cart-summary/cart drawer
      const container =
        this.closest('cart-summary') ||
        this.closest('.cart-totals__container') ||
        this.closest('.cart-totals') ||
        this.parentElement ||
        document.body;

      if (this._containerObs) this._containerObs.disconnect();

      this._containerObs = new MutationObserver(() => {
        if (!this._initialized) this._findAndBindSubtotal();
        this._updateFromSources();
      });

      this._containerObs.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
    }

    _findAndBindSubtotal() {
      if (this._initialized) return;

      let el = null;

      const container =
        this.closest('cart-summary') ||
        this.closest('.cart-totals__container') ||
        this.closest('.cart-totals');

      if (container) el = container.querySelector('[data-cart-subtotal]');

      if (!el) el = this.querySelector('[data-cart-subtotal]');
      if (!el) el = document.querySelector('[data-cart-subtotal]');
      if (!el) return;

      this._subtotalEl = el;
      this._initialized = true;

      if (this._subtotalObs) this._subtotalObs.disconnect();
      this._subtotalObs = new MutationObserver(() => this._updateFromSources());
      this._subtotalObs.observe(el, {
        attributes: true,
        attributeFilter: ['value'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    _refs() {
      return {
        messageEl: this.querySelector('[data-message]'),
        barEl: this.querySelector('[data-progress-bar]')
      };
    }

    _uiLooksStale(messageEl, barEl) {
      const msg = (messageEl?.textContent || '').trim().toLowerCase();
      const width = (barEl?.style?.width || '').trim();
      // If theme re-renders the snippet, it reintroduces the placeholder and clears inline width
      return !width || msg.includes('checking delivery');
    }

    _readSubtotalString() {
      if (!this._subtotalEl) return null;
      const attr = this._subtotalEl.getAttribute('value');
      if (attr && String(attr).trim()) return String(attr).trim();
      const txt = (this._subtotalEl.textContent || '').trim();
      return txt || null;
    }

    _parseMoneyToPence(text) {
      if (!text) return null;
      const cleaned = String(text).replace(/\s+/g, ' ').trim();
      const m = cleaned.match(/(\d[\d,]*[.\,]?\d*)/);
      if (!m) return null;

      let num = m[1];
      if (num.includes(',') && num.includes('.')) num = num.replace(/,/g, '');
      else if (num.includes(',') && !num.includes('.')) num = num.replace(',', '.');

      const value = Number.parseFloat(num);
      if (!Number.isFinite(value)) return null;
      return Math.round(value * 100);
    }

    _formatPence(pence) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        const tmp = document.createElement('div');
        tmp.innerHTML = String(Shopify.formatMoney(pence));
        const t = (tmp.textContent || '').trim();
        if (t) return t;
      }
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(pence / 100);
    }

    async _fetchCartTotalPence() {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        const cart = await res.json();
        return cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
      } catch (_) {
        return null;
      }
    }

    _scheduleRetry() {
      this._retryCount += 1;
      if (this._retryCount > 20) return; // ~2s max

      clearTimeout(this._retryTimer);
      this._retryTimer = setTimeout(() => this.requestUpdate(), 100);
    }

    async _updateFromSources() {
      if (!this.threshold) return;

      const { messageEl, barEl } = this._refs();
      if (!messageEl || !barEl) {
        this._scheduleRetry();
        return;
      }

      // If the theme has just re-rendered the snippet, force a repaint
      const staleUI = this._uiLooksStale(messageEl, barEl);

      // Try DOM subtotal first
      const str = this._readSubtotalString();
      const totalFromDom = this._parseMoneyToPence(str);

      let total = null;

      if (totalFromDom != null) {
        total = totalFromDom;
      } else {
        // Fallback to API whenever DOM subtotal is unavailable/late
        const totalFromApi = await this._fetchCartTotalPence();
        if (totalFromApi == null) {
          this._scheduleRetry();
          return;
        }
        total = totalFromApi;
      }

      this._retryCount = 0;

      const shouldRender = !this._hasRendered || staleUI || this._lastTotal !== total;
      if (!shouldRender) return;

      this._lastTotal = total;
      this._render(total);
    }

    _render(cartTotalPence) {
  const { messageEl, barEl } = this._refs();
  if (!messageEl || !barEl) return;

  this._hasRendered = true;

  const remaining = this.threshold - cartTotalPence;
  const pct = Math.min((cartTotalPence / this.threshold) * 100, 100);
  barEl.style.width = pct + '%';

  if (remaining <= 0) {
    barEl.classList.add('complete');
    messageEl.innerHTML = this.messageAfter;
    return;
  }

  barEl.classList.remove('complete');
  const formatted = this._formatPence(remaining);
  messageEl.innerHTML = this.messageBefore.replace(
    '[amount]',
    `<span class="fs-amt-${this.dataset.id || 'cartdrawer'}">${formatted}</span>`
  );
}
  }

  // Define once
  if (!customElements.get('free-shipping-progress')) {
    customElements.define('free-shipping-progress', FreeShippingProgress);
  }

  const upgradeAndKick = (root) => {
    const list = [];

    if (root instanceof Document) {
      root.querySelectorAll('free-shipping-progress').forEach((el) => list.push(el));
    } else if (root instanceof Element) {
      if (root.matches?.('free-shipping-progress')) list.push(root);
      root.querySelectorAll?.('free-shipping-progress').forEach((el) => list.push(el));
    }

    for (const el of list) {
      customElements.upgrade(el);
      setTimeout(() => {
        try { el.requestUpdate?.(); } catch (_) {}
      }, 0);
    }
  };

  // Existing nodes now
  upgradeAndKick(document);

  // Also run after DOM is fully loaded (catches late script loads)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => upgradeAndKick(document));
  }

  // And after full page load (catches everything)
  window.addEventListener('load', () => upgradeAndKick(document));

  // Cart drawer specific events (Atelier theme)
  document.addEventListener('cart:updated', () => {
    setTimeout(() => upgradeAndKick(document), 50);
  });
  document.addEventListener('cart:refresh', () => {
    setTimeout(() => upgradeAndKick(document), 50);
  });

  // Any nodes inserted later (cart drawer/cart-summary swaps)
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        upgradeAndKick(n);
      }
    }
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });
})();